import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Luggage, Pencil, Plus, Search, Shirt, Trash2, X } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const SEASONS = ["Primavera", "Estate", "Autunno", "Inverno"];
const SEXES = [["femmina", "Bambina"], ["maschio", "Bambino"], ["unisex", "Unisex"]];
const EMPTY = { title: "", description: "", sex: "unisex", seasons: [], items: [], active: true };
const KIND = { look: { label: "Look", icon: Shirt }, bag: { label: "Valigie", icon: Luggage } };

const Picker = ({ onPick, picked }) => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) return setRes([]);
    const t = setTimeout(() => api.get("/admin/products", { params: { q, limit: 12 } }).then(({ data }) => setRes(data.products)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div>
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B8ADA5]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca un capo da aggiungere (es. body, copertina)" className="pl-9" data-testid="curated-search" />
      </div>
      {res.length > 0 && (
        <div className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-[#EAE3DC] divide-y divide-[#F4EFEA]" data-testid="curated-results">
          {res.map((p) => (
            <button key={p.id} type="button" disabled={picked.has(String(p.id))} onClick={() => onPick(p)} data-testid={`curated-pick-${p.id}`}
              className="w-full flex items-center gap-3 p-2 text-left hover:bg-[#FAF8F5] disabled:opacity-40">
              {p.image && <img src={p.image} alt="" className="w-9 h-9 rounded-lg object-cover" />}
              <span className="text-sm text-[#2D2825] flex-1 line-clamp-1">{p.title}</span>
              <span className="text-[11px] text-[#8C7F78]">{p.available ? "" : "esaurito"}</span>
              <Plus size={15} className="text-[#4E7363]" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Chip = ({ on, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`rounded-full px-3 py-1 text-xs border transition-colors ${on ? "bg-[#4E7363] text-white border-[#4E7363]" : "bg-white text-[#5C534E] border-[#EAE3DC]"}`}>{children}</button>
);

const Editor = ({ kind, initial, onDone }) => {
  const [f, setF] = useState(initial || EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((cur) => ({ ...cur, [k]: v }));
  const toggleSeason = (s) => set("seasons", f.seasons.includes(s) ? f.seasons.filter((x) => x !== s) : SEASONS.filter((x) => f.seasons.includes(x) || x === s));
  const add = (p) => set("items", [...f.items, { product_id: String(p.id), title: p.title, image: p.image, quantity: 1, note: "" }]);
  const upd = (i, k, v) => set("items", f.items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const save = async () => {
    if (!f.title.trim()) return toast.error("Dai un nome");
    if (!f.seasons.length) return toast.error("Scegli almeno una stagione");
    if (!f.items.length) return toast.error("Aggiungi almeno un capo");
    setBusy(true);
    const body = { kind, title: f.title, description: f.description || null, sex: f.sex, seasons: f.seasons, active: f.active,
      items: f.items.map((i) => ({ product_id: i.product_id, quantity: Number(i.quantity) || 1, note: i.note || null })) };
    try {
      if (initial?.id) await api.put(`/admin/curated/${initial.id}`, body); else await api.post("/admin/curated", body);
      toast.success(`${kind === "look" ? "Look" : "Valigia"} salvata: le mamme la vedono subito`);
      onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Controlla i campi");
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-[#EAE3DC] bg-[#FDFBF9] p-4 sm:p-5 space-y-4" data-testid="curated-editor">
      <div className="grid sm:grid-cols-2 gap-3">
        <Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder={kind === "look" ? "Nome del look (es. Nuvola rosa)" : "Nome della valigia (es. Valigia inverno bambina)"} data-testid="curated-title" />
        <select value={f.sex} onChange={(e) => set("sex", e.target.value)} className="h-10 rounded-xl border border-[#EAE3DC] bg-white text-sm px-3" data-testid="curated-sex">
          {SEXES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <textarea value={f.description || ""} onChange={(e) => set("description", e.target.value)} maxLength={400} rows={2}
        placeholder="Due righe per le mamme (facoltativo)" className="w-full rounded-xl border border-[#EAE3DC] bg-white text-sm p-3" data-testid="curated-description" />
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-[#8C7F78] mr-1">Stagioni:</span>
        {SEASONS.map((s) => <Chip key={s} on={f.seasons.includes(s)} onClick={() => toggleSeason(s)} testid={`curated-season-${s}`}>{s}</Chip>)}
        <label className="ml-auto flex items-center gap-2 text-xs text-[#5C534E]">
          <input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} data-testid="curated-active" /> Visibile alle mamme
        </label>
      </div>
      <Picker onPick={add} picked={new Set(f.items.map((i) => i.product_id))} />
      <div className="space-y-2" data-testid="curated-items">
        {f.items.map((it, i) => (
          <div key={it.product_id} className="flex flex-wrap sm:flex-nowrap items-center gap-2 rounded-xl bg-white border border-[#F1ECE7] p-2">
            {it.image && <img src={it.image} alt="" className="w-10 h-10 rounded-lg object-cover" />}
            <span className="text-sm text-[#2D2825] flex-1 min-w-[140px] line-clamp-1">{it.title}</span>
            {kind === "bag" && (
              <Input type="number" min={1} max={10} value={it.quantity} onChange={(e) => upd(i, "quantity", e.target.value)} className="w-16 h-9" aria-label="Quantità" data-testid={`curated-qty-${it.product_id}`} />
            )}
            <Input value={it.note || ""} onChange={(e) => upd(i, "note", e.target.value)} maxLength={120} placeholder="Perché questo capo (facoltativo)" className="h-9 sm:w-64" />
            <button type="button" onClick={() => set("items", f.items.filter((_, j) => j !== i))} aria-label="Togli" className="p-2 text-[#B8ADA5] hover:text-[#B85750]"><X size={15} /></button>
          </div>
        ))}
        <p className="text-[11px] text-[#8C7F78]">La taglia la calcola l'app per ogni mamma: se un capo non ha la sua taglia, per lei viene saltato.</p>
      </div>
      <div className="flex gap-2">
        <Button onClick={save} disabled={busy} className="rounded-full bg-[#2D2825] hover:bg-[#473f3a]" data-testid="curated-save">
          {busy ? <Loader2 className="animate-spin" size={15} /> : "Salva"}
        </Button>
        <Button variant="ghost" onClick={onDone} className="rounded-full" data-testid="curated-cancel">Annulla</Button>
      </div>
    </div>
  );
};

const SetRow = ({ s, onEdit, onChange }) => {
  const remove = async () => {
    if (!window.confirm(`Eliminare "${s.title}"?`)) return;
    try { await api.delete(`/admin/curated/${s.id}`); toast.success("Eliminato"); onChange(); } catch { toast.error("Errore"); }
  };
  return (
    <div className="flex items-center gap-3 rounded-xl border border-[#EAE3DC] bg-white p-3" data-testid={`curated-row-${s.id}`}>
      <div className="flex -space-x-2">{s.items.slice(0, 4).map((i) => i.image && <img key={i.product_id} src={i.image} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-white" />)}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[#2D2825] truncate">{s.title} {!s.active && <span className="text-[11px] text-[#B07A3B]">· nascosto</span>}</div>
        <div className="text-[11px] text-[#8C7F78]">{SEXES.find(([v]) => v === s.sex)?.[1]} · {s.seasons.join(", ")} · {s.items.length} {s.items.length === 1 ? "capo" : "capi"}</div>
      </div>
      <button onClick={onEdit} aria-label="Modifica" className="p-2 text-[#8C7F78] hover:text-[#2D2825]" data-testid={`curated-edit-${s.id}`}><Pencil size={15} /></button>
      <button onClick={remove} aria-label="Elimina" className="p-2 text-[#B8ADA5] hover:text-[#B85750]" data-testid={`curated-delete-${s.id}`}><Trash2 size={15} /></button>
    </div>
  );
};

export const CuratedAdmin = () => {
  const [kind, setKind] = useState("look");
  const [sets, setSets] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/curated", { params: { kind } }).then(({ data }) => setSets(data.items)).catch(() => setSets([])), [kind]);
  useEffect(() => { setSets(null); setEditing(null); load(); }, [load]);
  const done = () => { setEditing(null); load(); };

  return (
    <section className="rounded-2xl border border-[#EAE3DC] bg-white p-5 sm:p-6 mb-6 space-y-4" data-testid="curated-admin">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <h3 className="font-medium text-[#2D2825]">Look e valigie create da te</h3>
        <div className="flex gap-1.5">
          {Object.entries(KIND).map(([k, { label, icon: I }]) => (
            <button key={k} onClick={() => setKind(k)} data-testid={`curated-tab-${k}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm border transition-colors ${kind === k ? "bg-[#2D2825] text-white border-[#2D2825]" : "bg-white text-[#5C534E] border-[#EAE3DC]"}`}>
              <I size={14} /> {label}
            </button>
          ))}
        </div>
      </div>
      <p className="text-sm text-[#5C534E]">
        Le mamme del sesso e della stagione giusti li vedono {kind === "look" ? "nella sezione Look" : "come “La valigia consigliata da Minime”"}, con i capi nella loro taglia.
        L'AI li usa come {kind === "look" ? "esempio per creare i look personalizzati" : "base per comporre la valigia personalizzata"}.
      </p>
      {editing ? <Editor kind={kind} initial={editing === "new" ? null : editing} onDone={done} /> : (
        <button onClick={() => setEditing("new")} data-testid="curated-new"
          className="inline-flex items-center gap-2 rounded-full bg-[#2D2825] text-white text-sm px-5 py-2 hover:bg-[#473f3a]">
          <Plus size={15} /> {kind === "look" ? "Crea un look" : "Componi una valigia"}
        </button>
      )}
      {!sets ? <Loader2 className="animate-spin text-[#8C7F78]" size={18} /> : sets.length === 0 ? (
        <p className="text-sm text-[#8C7F78]" data-testid="curated-empty">Ancora niente qui.</p>
      ) : <div className="space-y-2">{sets.map((s) => <SetRow key={s.id} s={s} onEdit={() => setEditing(s)} onChange={load} />)}</div>}
    </section>
  );
};
